<template>
  <div id="infobits-content">
    <detail-panel></detail-panel>
    <tree-panel></tree-panel>
    <inbox></inbox>
  </div>
</template>

<script>
import Vue from 'vue'
import VueDragula from 'vue-dragula'

Vue.use(VueDragula)

const DragAction = {
  MOVE: Symbol('MOVE'),
  COPY: Symbol('COPY'),
  TRANSCLUDE: Symbol('TRANSCLUDE')
}

const CursorShape = {
  [DragAction.MOVE]: 'row-resize',
  [DragAction.COPY]: 'copy',
  [DragAction.TRANSCLUDE]: 'alias'
}

export default {

  data () {
    return {
      // drag state (affects cursor shape)
      dragging:   false,      // true while drag in progress
      acceptDrop: false,      // true if current target accepts drop
      action:     undefined,  // a DragAction; initialized on drag start (see copy() callback in created() hook)
      //
      // (vue-)dragula event handlers
      events: {
        'drop-model': this.dropModel,   // vue-dragula event
        'drag':       this.drag,        // dragula event
        'dragend':    this.dragend,     // dragula event
        'cloned':     this.cloned,      // dragula event
        'shadow':     this.shadow       // dragula event
      }
    }
  },

  computed: {

    loggedInUser () {
      return this.$store.state.user
    },

    modKeys () {
      return this.$store.state.modKeys
    },

    shiftKey () {
      return this.modKeys.shiftKey
    },

    altKey () {
      return this.modKeys.altKey
    }
  },

  created () {
    // Initialize dragula and setup the "accepts" and "copy" callbacks.
    // Note: in contrast to the dragula event handlers these callbacks compute and return a value.
    this.$dragula.options('infobits', {

      // while drag is in progress computes continiously whether a drop is allowed
      accepts: (el, target, source, sibling) => {
        var panel = this.panel(target)
        var acceptDrop
        if (panel === 'tree') {
          if (this.loggedInUser) {          // tree must be writable
            if (!el.contains(target)) {     // element can't be dropped on self
              acceptDrop = true
            }
          }
        } else if (panel === 'detail') {
          acceptDrop = true
        }
        this.acceptDrop = acceptDrop
        this.updateCursor()
        return acceptDrop
      },

      // once a drag starts computes weather the dragged element should be copied
      copy: (el, source) => {
        if (this.panel(source) === 'inbox') {
          this.action = DragAction.MOVE
          return true
        } else {
          this.action = this.shiftKey ? this.altKey ? DragAction.TRANSCLUDE : DragAction.COPY : DragAction.MOVE
          return this.action === DragAction.COPY || this.action === DragAction.TRANSCLUDE
        }
      }
    })
    // register (vue-)dragula event handlers
    this.eventHandlers('$on')
  },

  beforeDestroy () {
    // unregister (vue-)dragula event handlers
    this.eventHandlers('$off')
  },

  methods: {

    // === (vue-)dragula event handlers ===

    /**
     * Vue-dragula handler fired once an element was dropped.
     * Vue view models are available as "dropTarget.model" and "dropSource.model".
     */
    dropModel (bagName, el, dropTarget, dropSource, dropIndex) {
      // 1) Calculates items involved in drop
      // - Dropped into 'tree': calculate "dropped", "parentNode", "predNodeId"
      // - Dropped into 'drop-link-area': calculate "source" (selected infobit) and "target" infobits
      var dropped = dropTarget.model[dropIndex]   // either a node (source=tree) or an infobit (source=inbox)
      var parentNode
      var predNodeId
      var cl = dropTarget.el.classList
      var isLinkDrop = false
      var targetInfobitId = -1
      if (cl.contains('infobits-tree') || cl.contains('empty-tree-drop-area')) {
        parentNode = this.$store.state.treePanel.tree            // dropped between top-level childs
      } else if (cl.contains('child-nodes')) {
        parentNode = dropTarget.el.parentElement.__vue__.node    // dropped between deeper-level childs
      } else if (cl.contains('drop-area')) {
        parentNode = dropTarget.el.parentElement.parentElement.__vue__.node // dropped into empty tree
      } else if (cl.contains('drop-link-area')) { // dropped into create-infobit link area
        isLinkDrop = true
        targetInfobitId = (this.panel(dropSource.el) === 'inbox') ? dropped.id : dropped.infobit.id
      } else {
        throw new Error('Unknown drop target: ' + dropTarget.el)
      }
      predNodeId = dropIndex > 0 ? dropTarget.model[dropIndex - 1].id : -1
      // 2.1) dispatch a 'link two infobits' action (and maybe refactor...)
      if (isLinkDrop) {
        var selectedInfobit = this.$store.state.detailPanel.infobit
        this.$store.dispatch('fetchInfobit', {infobitId: targetInfobitId}).then(targetInfobit => {
          this.$store.dispatch('createInfobitLink', {sourceInfobit: selectedInfobit, targetInfobit})
        })
      // 2.2) dispatch a 'drop single infobit from inbox'
      } else if (this.panel(dropSource.el) === 'inbox') {
        console.log('insertInfobitInTree(infobitId, parentNode, predNodeId)', dropped.id, parentNode, predNodeId)
        this.$store.dispatch('insertInfobitInTree', {infobitId: dropped.id, parentNode: parentNode, predNodeId})
      // 2.3) dispatch a 'drop infobit or tree within tree' action
      } else if (this.panel(dropSource.el) === 'tree') {
        switch (this.action) {
          case DragAction.MOVE:
            console.log('moveSubtree(rootNode, parentNode, predNodeId)', dropped, parentNode, predNodeId)
            this.$store.dispatch('moveSubtree', {rootNode: dropped, parentNode: parentNode, predNodeId})
            break
          case DragAction.COPY:
            console.log('copySubtree(rootNode, parentNode, predNodeId)', dropped, parentNode, predNodeId)
            this.$store.dispatch('copySubtree', {rootNode: dropped, parentNode, predNodeId})
            break
          case DragAction.TRANSCLUDE:
            console.log('createTransclusion(rootNode, parentNode, predNodeId)', dropped, parentNode, predNodeId)
            this.$store.dispatch('createTransclusion', {rootNode: dropped, parentNode, predNodeId})
            break
          default:
            throw new Error(`Unexpected drag action: ${this.action && this.action.toString()}`)
        }
      } else {
        console.error('Drop action from', dropSource.el, 'not yet implemented, or "predNodeId" is not set', predNodeId)
      }
    },

    /**
     * Fired once an element was lifted from source, that is a drag starts.
     */
    drag (bagName, el, source) {
      // Note: el is the original element. To manipulate the mirror element use the "cloned" event.
      this.dragging = true
      this.updateCursor()
    },

    /**
     * Fired once a drag ends.
     */
    dragend (bagName, el) {
      this.dragging = false
      this.updateCursor()
    },

    /**
     * Fired once after drag start when the mirror element is cloned from the original element.
     * The "mirror element" is the element that follows the mouse while dragging.
     */
    cloned (bagName, clone, original, type) {
      // remove infobit hover behavior from mirror element
      if (type === 'mirror') {
        clone.querySelector('.infobit').classList.add('no-events')
      }
    },

    /**
     * Fired each time when the "feedback shadow" element (also called "visual aid shadow") is placed while a drag.
     * "el" is the shadow element.
     */
    shadow (bagName, el, container, source) {
      // when dragged from inbox adapt shadow element to tree style
      if (this.panel(source) === 'inbox') {
        el.classList.add('inbox-transit')
      }
    },

    // === Helper ===

    /**
     * Returns "tree", "inbox", or "detail" based on where the given DOM element belongs to.
     */
    panel (el) {
      if (el.classList.contains('infobits-tree') ||
          el.classList.contains('child-nodes') ||
          el.classList.contains('drop-area') ||
          el.classList.contains('empty-tree-drop-area')) {
        return 'tree'
      } else if (el.parentElement.id === 'inbox') {
        return 'inbox'
      } else if (el.classList.contains('drop-link-area')) {
        return 'detail'
      } else {
        console.warn('Unknown dragula container', el, el.parentElement.id, el.classList)
      }
    },

    /**
     * Updates the cursor shape based on the drag state ("dragging", "acceptDrop", "action").
     * Note: must be called manually every time the drag state changes. Binding via Vue does not work (see note below).
     */
    updateCursor () {
      // Note: updating the cursor shape via binding the style attribute does not work properly. I don't know why.
      // See the stash "cursor-style-binding". It does only work when setting the body style, but the body element
      // is not covered by our Vue root instance. So we do the update programmatically here.
      var cursor = this.dragging ? this.acceptDrop ? CursorShape[this.action] : 'no-drop' : 'auto'
      // console.log('cursor', cursor)
      document.body.style.cursor = cursor
    },

    /**
     * Registers and unregisters the (vue-)dragula event handlers.
     */
    eventHandlers (funcName) {
      var eb = this.$dragula.eventBus
      for (var event in this.events) {
        eb[funcName](event, this.events[event])
      }
    }
  },

  components: {
    'tree-panel':   require('./tree-panel.vue'),
    'inbox':        require('./inbox.vue'),
    'detail-panel': require('./detail-panel.vue')
  }
}
</script>

<style src='dragula/dist/dragula.css'></style>

<style>
#infobits-content {
  flex: auto;
  display: flex;
  min-height: 0;
}
</style>



// WEBPACK FOOTER //
// infobits-content.vue?cebb8d2c